import { Box, Tooltip } from "@mui/material";
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import moment from "moment";
import { UserMessage } from "@bt-common/schemas/bt/user-message";
import {
  MESSAGE_STATUS_CONFIG,
  getEffectiveMessageStatus,
  EffectiveMessageStatus
} from "@/common/components/message-status/message-status-config";
import { useTranslation } from 'react-i18next';
import { namespace } from '@common-config/constants';
import { getMomentLocale } from '@common-config/moment-locales';

interface RelativeTimestampBadgeProps {
  dueDate?: string;
  compact?: boolean; // For single-line display in tight spaces
  status?: EffectiveMessageStatus; // Message status (pending, sending, sent, failed, expired, etc.)
  error?: any; // Error message if status is failed
  message?: UserMessage; // Full message object for computing effective status
}

const RelativeTimestampBadge = ({ dueDate, compact = false, status, error, message }: RelativeTimestampBadgeProps) => {
  const { t, i18n } = useTranslation(namespace);

  if (!dueDate) return null;

  const language = i18n.resolvedLanguage || i18n.language || 'en';
  const dateMoment = moment(dueDate).locale(getMomentLocale(language));
  const formattedDate = dateMoment.format("HH:mm DD/MM/YYYY");
  const relativeTime = dateMoment.fromNow();

  // Compute effective status: if message is provided, use it to detect expired status
  const effectiveStatus = message ? getEffectiveMessageStatus(message) : status;

  const statusConfig = effectiveStatus ? MESSAGE_STATUS_CONFIG[effectiveStatus] : null;
  const StatusIcon = statusConfig?.icon;
  const hasError = (effectiveStatus === 'failed' || effectiveStatus === 'expired') && error;

  if (compact) {
    // Single-line compact version (for very tight spaces)
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            backgroundColor: 'var(--date-badge-bg)',
            padding: '2px 8px',
            fontSize: '11px',
            fontWeight: 500,
            color: 'var(--date-badge-text)',
            border: '1px solid var(--date-badge-border)',
            borderRadius: '6px',
            height: '22px',
            whiteSpace: 'nowrap',
            '& svg': {
              fontSize: '13px',
              marginInlineEnd: '4px',
              color: 'var(--date-badge-icon)'
            }
          }}
        >
          <AccessTimeIcon fontSize="inherit" />
          {relativeTime}
        </Box>
        
        {/* Status indicator with error tooltip if applicable */}
        {StatusIcon && (
          <Tooltip
            title={hasError ? (typeof error === 'object' ? JSON.stringify(error) : String(error)) : t(statusConfig.description)}
            arrow
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '3px',
                cursor: hasError || effectiveStatus === 'expired' ? 'help' : 'default',
              }}
            >
              <StatusIcon
                size={14}
                style={{
                  color: statusConfig.color,
                  animation: statusConfig.iconClassName === 'animate-spin' ? 'spin 1s linear infinite' : undefined
                }}
              />
              <Box
                sx={{
                  fontSize: '10px',
                  fontWeight: 600,
                  color: statusConfig.color,
                }}
              >
                {t(statusConfig.label)}
              </Box>
            </Box>
          </Tooltip>
        )}
      </Box>
    );
  }

  // Two-line version with relative time and absolute timestamp
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '2px',
        backgroundColor: 'var(--calendar-date-badge-bg)',
        padding: '6px 10px',
        borderRadius: '8px',
        border: '1px solid var(--date-badge-border)',
        minWidth: 0,
      }}
    >
      {/* Relative time with status icon */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        fontSize: '12px',
        fontWeight: 600,
        color: 'var(--calendar-date-badge-text)',
        gap: '4px',
      }}>
        <AccessTimeIcon sx={{ fontSize: '14px' }} />
        {relativeTime}

        {/* Status indicator with error tooltip if applicable */}
        {StatusIcon && (
          <Tooltip
            title={hasError ? (typeof error === 'object' ? JSON.stringify(error) : String(error)) : t(statusConfig.description)}
            arrow
          >
            <Box
              component="span"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '3px',
                marginInlineStart: '4px',
                cursor: hasError || effectiveStatus === 'expired' ? 'help' : 'default',
              }}
            >
              <StatusIcon
                size={14}
                style={{
                  color: statusConfig.color,
                  animation: statusConfig.iconClassName === 'animate-spin' ? 'spin 1s linear infinite' : undefined
                }}
              />
              <Box
                component="span"
                sx={{
                  fontSize: '10px',
                  fontWeight: 600,
                  color: statusConfig.color,
                }}
              >
                {t(statusConfig.label)}
              </Box>
            </Box>
          </Tooltip>
        )}
      </Box>

      {/* Absolute timestamp */}
      <Box sx={{
        fontSize: '10px',
        fontWeight: 500,
        color: 'var(--color-text-muted)',
        opacity: 0.8,
        whiteSpace: 'nowrap',
      }}>
        {formattedDate}
      </Box>
    </Box>
  );
};

export default RelativeTimestampBadge;
